import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { Skeleton } from '@heroui/react'
import { useStatsigClient } from '@statsig/react-bindings'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { useAuth } from 'hooks/useAuth'
import { Calendar, Code, Network, StarIcon, X } from 'lucide-react'
import { useContext, useEffect, useRef, useState } from 'react'
import { CiRoute } from 'react-icons/ci'
import { $fetch } from 'utils/fetch'
import NodePreviewCard from './Components/NodePreviewCard'
import PreConfiguredNodesTab from './Components/PreConfiguredNodesTab'
import LibrarySidebarContext from './contexts/librarySidebarContext'

const nodeTypes = [
  {
    type: 'Default',
    name: 'Default Node',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22.1492 14.4298C22.9909 14.5941 23.7644 15.0057 24.3708 15.6121C24.9772 16.2185 25.3888 16.992 25.5531 17.8337M22.1492 10.9829C23.8979 11.1772 25.5286 11.9603 26.7735 13.2036C28.0184 14.4469 28.8035 16.0766 29 17.8251M28.1383 24.7017V27.2869C28.1392 27.5269 28.0901 27.7645 27.9939 27.9844C27.8978 28.2043 27.7568 28.4016 27.5799 28.5639C27.4031 28.7261 27.1943 28.8497 26.967 28.9265C26.7396 29.0034 26.4987 29.032 26.2597 29.0104C23.608 28.7223 21.0608 27.8161 18.8229 26.3649C16.7408 25.0418 14.9756 23.2766 13.6525 21.1945C12.1962 18.9464 11.2899 16.3868 11.007 13.7232C10.9855 13.4849 11.0138 13.2448 11.0902 13.018C11.1665 12.7912 11.2893 12.5829 11.4506 12.4062C11.6119 12.2295 11.8082 12.0883 12.0271 11.9916C12.246 11.8949 12.4826 11.8449 12.7218 11.8446H15.307C15.7253 11.8405 16.1307 11.9886 16.4478 12.2613C16.7649 12.534 16.972 12.9127 17.0305 13.3268C17.1396 14.1541 17.342 14.9665 17.6337 15.7483C17.7497 16.0567 17.7748 16.3919 17.706 16.7142C17.6373 17.0365 17.4776 17.3323 17.2459 17.5666L16.1515 18.661C17.3783 20.8184 19.1646 22.6046 21.322 23.8314L22.4164 22.737C22.6506 22.5053 22.9465 22.3456 23.2687 22.2769C23.591 22.2081 23.9262 22.2332 24.2346 22.3492C25.0164 22.6409 25.8288 22.8433 26.6561 22.9524C27.0747 23.0114 27.457 23.2223 27.7303 23.5448C28.0035 23.8674 28.1488 24.2791 28.1383 24.7017Z"
          stroke="#4B5563"
          stroke-width="1.54286"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    type: 'Knowledge Base',
    name: 'Large Text',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.9091 15.0905C15.9091 14.8735 15.9953 14.6653 16.1487 14.5119C16.3022 14.3585 16.5103 14.2723 16.7273 14.2723H24.3636C24.5806 14.2723 24.7887 14.3585 24.9422 14.5119C25.0956 14.6653 25.1818 14.8735 25.1818 15.0905C25.1818 15.3074 25.0956 15.5156 24.9422 15.669C24.7887 15.8224 24.5806 15.9086 24.3636 15.9086H16.7273C16.5103 15.9086 16.3022 15.8224 16.1487 15.669C15.9953 15.5156 15.9091 15.3074 15.9091 15.0905ZM16.7273 17.545C16.5103 17.545 16.3022 17.6312 16.1487 17.7846C15.9953 17.9381 15.9091 18.1462 15.9091 18.3632C15.9091 18.5802 15.9953 18.7883 16.1487 18.9417C16.3022 19.0952 16.5103 19.1814 16.7273 19.1814H22.1818C22.3988 19.1814 22.6069 19.0952 22.7604 18.9417C22.9138 18.7883 23 18.5802 23 18.3632C23 18.1462 22.9138 17.9381 22.7604 17.7846C22.6069 17.6312 22.3988 17.545 22.1818 17.545H16.7273Z"
          fill="#4B5563"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M16.1818 9.90918C14.8075 9.90918 13.4895 10.4551 12.5177 11.4269C11.5459 12.3987 11 13.7167 11 15.091V26.0001C11 27.0851 11.431 28.1256 12.1982 28.8928C12.9654 29.66 14.0059 30.091 15.0909 30.091H27.0909C27.5972 30.091 28.0828 29.8899 28.4408 29.5318C28.7989 29.1738 29 28.6882 29 28.1819V11.8183C29 11.3119 28.7989 10.8264 28.4408 10.4683C28.0828 10.1103 27.5972 9.90918 27.0909 9.90918H16.1818ZM27.3636 21.9092V11.8183C27.3636 11.7459 27.3349 11.6766 27.2838 11.6254C27.2326 11.5743 27.1632 11.5455 27.0909 11.5455H16.1818C15.2415 11.5455 14.3397 11.9191 13.6748 12.584C13.0099 13.2489 12.6364 14.1507 12.6364 15.091V22.7274C13.3439 22.1951 14.2056 21.9079 15.0909 21.9092H27.3636ZM27.3636 23.5455H15.0909C14.4399 23.5455 13.8156 23.8041 13.3553 24.2645C12.895 24.7248 12.6364 25.3491 12.6364 26.0001C12.6364 26.6511 12.895 27.2754 13.3553 27.7357C13.8156 28.196 14.4399 28.4546 15.0909 28.4546H27.0909C27.1632 28.4546 27.2326 28.4259 27.2838 28.3748C27.3349 28.3236 27.3636 28.2542 27.3636 28.1819V23.5455Z"
          fill="#4B5563"
        />
      </svg>
    ),
  },
  {
    type: 'Vector DB Knowledge Base',
    name: 'Knowledge Base',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M20 16C24.4183 16 28 14.6569 28 13C28 11.3431 24.4183 10 20 10C15.5817 10 12 11.3431 12 13C12 14.6569 15.5817 16 20 16Z"
          stroke="#4B5563"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M28 20C28 21.657 24.418 23 20 23C15.582 23 12 21.657 12 20"
          stroke="#4B5563"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M28 13V27C28 28.657 24.418 30 20 30C15.582 30 12 28.657 12 27V13M16 16V18M16 23V25"
          stroke="#4B5563"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    type: 'Transfer Call',
    name: 'Transfer Call',
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7 9L3 5M3 5L7 1M3 5H16C18.7614 5 21 7.23858 21 10V10"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M17 15L21 19M21 19L17 23M21 19H8C5.23858 19 3 16.7614 3 14V14"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    type: 'End Call',
    name: 'End Call',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M13.066 16.5605C14.8784 15.731 17.3458 15.2505 19.9998 15.2505C22.6537 15.2505 25.1216 15.731 26.9341 16.5605C28.6935 17.3654 30.079 18.6284 29.9965 20.2919C29.9958 20.3069 29.9945 20.3215 29.9925 20.3358L29.713 22.6583C29.6747 22.9762 29.5725 23.2831 29.4126 23.5605C29.2526 23.8379 29.0382 24.0801 28.7823 24.2725C28.5264 24.4649 28.2341 24.6036 27.9232 24.6802C27.6123 24.7568 27.2891 24.7697 26.9731 24.7182L24.4171 24.3017C23.9925 24.2325 23.5989 24.0359 23.2884 23.7381C22.9779 23.4402 22.7651 23.0552 22.6782 22.6338L22.2107 20.3673C22.2086 20.3572 22.2049 20.3474 22.1997 20.3384C21.8227 20.2089 21.0672 20.0089 19.9998 20.0089C18.9328 20.0089 18.1773 20.2089 17.7998 20.3389C17.7951 20.3478 17.7917 20.3574 17.7898 20.3673L17.3218 22.6338C17.2349 23.0552 17.0221 23.4402 16.7116 23.7381C16.4011 24.0359 16.0075 24.2325 15.5829 24.3017L13.027 24.7187C12.7109 24.7702 12.3877 24.7573 12.0767 24.6807C11.7658 24.604 11.4735 24.4653 11.2176 24.2728C10.9616 24.0804 10.7473 23.8381 10.5873 23.5606C10.4274 23.2832 10.3253 22.9762 10.287 22.6583L10.007 20.3354L10.0035 20.2919C9.92054 18.6284 11.3065 17.3654 13.066 16.5605ZM11.251 20.2104L11.5275 22.5088C11.5456 22.6595 11.594 22.8049 11.6698 22.9364C11.7456 23.0678 11.8472 23.1826 11.9685 23.2738C12.0898 23.365 12.2283 23.4307 12.3757 23.4669C12.523 23.5032 12.6762 23.5093 12.826 23.4848L15.3814 23.0678C15.5562 23.0393 15.7182 22.9584 15.8461 22.8358C15.9739 22.7132 16.0615 22.5547 16.0974 22.3813L16.5654 20.1149C16.6494 19.7074 16.9279 19.3174 17.3838 19.1599C17.8688 18.9924 18.7658 18.7589 19.9998 18.7589C21.2337 18.7589 22.1307 18.9924 22.6162 19.1599C23.0717 19.3174 23.3507 19.7074 23.4347 20.1149L23.9022 22.3813C23.938 22.5547 24.0256 22.7132 24.1535 22.8358C24.2813 22.9584 24.4433 23.0393 24.6181 23.0678L27.1741 23.4848C27.3238 23.5092 27.4769 23.5031 27.6242 23.4668C27.7715 23.4305 27.91 23.3648 28.0312 23.2736C28.1525 23.1824 28.254 23.0677 28.3298 22.9362C28.4055 22.8048 28.4539 22.6594 28.472 22.5088L28.7485 20.2099C28.7785 19.3734 28.0646 18.4519 26.4136 17.6969C24.8031 16.9599 22.5222 16.5005 19.9998 16.5005C17.4773 16.5005 15.1969 16.9599 13.5859 17.6969C11.9355 18.4519 11.221 19.3739 11.251 20.2104Z"
          fill="#4B5563"
        />
      </svg>
    ),
  },
  {
    type: 'Webhook',
    name: 'Webhook',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.4051 12.0043L19.9997 14.659L24.5943 12.0043M15.4051 27.9119V22.6128L10.8105 19.9581M29.1889 19.9581L24.5943 22.6128V27.9119M11.0862 14.8121L19.9998 19.9683L28.9133 14.8121M19.9997 30.25V19.9581M29.1889 24.0422V15.874C29.1886 15.5159 29.094 15.1642 28.9148 14.8542C28.7356 14.5442 28.478 14.2867 28.1679 14.1077L21.0208 10.0236C20.7103 9.84436 20.3582 9.75 19.9997 9.75C19.6413 9.75 19.2892 9.84436 18.9787 10.0236L11.8316 14.1077C11.5214 14.2867 11.2639 14.5442 11.0847 14.8542C10.9054 15.1642 10.8109 15.5159 10.8105 15.874V24.0422C10.8109 24.4003 10.9054 24.752 11.0847 25.062C11.2639 25.3721 11.5214 25.6295 11.8316 25.8086L18.9787 29.8927C19.2892 30.0719 19.6413 30.1663 19.9997 30.1663C20.3582 30.1663 20.7103 30.0719 21.0208 29.8927L28.1679 25.8086C28.478 25.6295 28.7356 25.3721 28.9148 25.062C29.094 24.752 29.1886 24.4003 29.1889 24.0422Z"
          stroke="#4B5563"
          stroke-width="1.41372"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    type: 'Wait for Response',
    name: 'Wait for Response',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M26.8053 14.9097V11.445C26.8053 11.084 26.6619 10.7378 26.4067 10.4826C26.1514 10.2274 25.8053 10.084 25.4443 10.084H14.5563C14.1954 10.084 13.8492 10.2274 13.5939 10.4826C13.3387 10.7378 13.1953 11.084 13.1953 11.445V14.9447C13.1958 15.1559 13.2452 15.3641 13.3396 15.553C13.4341 15.742 13.571 15.9064 13.7397 16.0335L19.0282 19.9998L13.7397 23.9662C13.571 24.0933 13.4341 24.2577 13.3396 24.4466C13.2452 24.6355 13.1958 24.8438 13.1953 25.055V28.5547C13.1953 28.9157 13.3387 29.2618 13.5939 29.5171C13.8492 29.7723 14.1954 29.9157 14.5563 29.9157H25.4443C25.8053 29.9157 26.1514 29.7723 26.4067 29.5171C26.6619 29.2618 26.8053 28.9157 26.8053 28.5547V25.09C26.8047 24.8796 26.7556 24.6722 26.6619 24.4839C26.5682 24.2956 26.4323 24.1314 26.2648 24.0041L20.9725 19.9998L26.2697 15.9956C26.4362 15.8678 26.5712 15.7034 26.6641 15.5151C26.757 15.3268 26.8053 15.1197 26.8053 14.9097ZM25.6387 25.09V28.5547C25.6387 28.6063 25.6183 28.6557 25.5818 28.6922C25.5453 28.7286 25.4959 28.7491 25.4443 28.7491H14.5563C14.5047 28.7491 14.4553 28.7286 14.4188 28.6922C14.3824 28.6557 14.3619 28.6063 14.3619 28.5547V25.055C14.3619 25.0248 14.3689 24.995 14.3824 24.968C14.3959 24.941 14.4155 24.9176 14.4397 24.8994L20.0003 20.7299L25.5629 24.9344C25.5867 24.9528 25.6059 24.9763 25.6191 25.0033C25.6322 25.0303 25.639 25.06 25.6387 25.09ZM25.6387 14.9097C25.6389 14.9398 25.632 14.9695 25.6186 14.9965C25.6053 15.0235 25.5859 15.047 25.5619 15.0652L20.0003 19.2698L14.4397 15.1002C14.4155 15.0821 14.3959 15.0586 14.3824 15.0316C14.3689 15.0047 14.3619 14.9749 14.3619 14.9447V11.445C14.3619 11.3934 14.3824 11.344 14.4188 11.3075C14.4553 11.271 14.5047 11.2506 14.5563 11.2506H25.4443C25.4959 11.2506 25.5453 11.271 25.5818 11.3075C25.6183 11.344 25.6387 11.3934 25.6387 11.445V14.9097Z"
          fill="#4B5563"
          stroke="#4B5563"
          stroke-width="0.566621"
        />
      </svg>
    ),
  },
  //   {
  //     type: 'Schedule Meeting',
  //     name: 'Schedule Meeting',
  //     icon: (
  //       <svg
  //         width="40"
  //         height="40"
  //         viewBox="0 0 40 40"
  //         fill="none"
  //         xmlns="http://www.w3.org/2000/svg"
  //       >
  //         <path
  //           stroke="#4B5563"
  //           stroke-width="1"
  //           d="M32.25 6h-4v3a2.2 2.2 0 1 1-4.4 0V6H12.2v3a2.2 2.2 0 0 1-4.4 0V6h-4A1.78 1.78 0 0 0 2 7.81v22.38A1.78 1.78 0 0 0 3.75 32h28.5A1.78 1.78 0 0 0 34 30.19V7.81A1.78 1.78 0 0 0 32.25 6M10 26H8v-2h2Zm0-5H8v-2h2Zm0-5H8v-2h2Zm6 10h-2v-2h2Zm0-5h-2v-2h2Zm0-5h-2v-2h2Zm6 10h-2v-2h2Zm0-5h-2v-2h2Zm0-5h-2v-2h2Zm6 10h-2v-2h2Zm0-5h-2v-2h2Zm0-5h-2v-2h2Z"
  //           class="clr-i-solid clr-i-solid-path-1"
  //         />
  //         <path
  //           stroke="#4B5563"
  //           stroke-width="1"
  //           d="M10 10a1 1 0 0 0 1-1V3a1 1 0 0 0-2 0v6a1 1 0 0 0 1 1"
  //           class="clr-i-solid clr-i-solid-path-2"
  //         />
  //         <path
  //           stroke="#4B5563"
  //           stroke-width="1"
  //           d="M26 10a1 1 0 0 0 1-1V3a1 1 0 0 0-2 0v6a1 1 0 0 0 1 1"
  //           class="clr-i-solid clr-i-solid-path-3"
  //         />
  //         <path fill="none" d="M0 0h36v36H0z" />
  //       </svg>
  //     ),
  //   },
  {
    type: 'Transfer Pathway',
    name: 'Transfer Pathway',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M17.6319 14.7602L17.2963 14.0052C17.0769 13.5116 16.9672 13.2647 16.8032 13.0758C16.5976 12.8391 16.3296 12.665 16.0297 12.5732C15.7905 12.5 15.5204 12.5 14.9802 12.5C14.1899 12.5 13.7948 12.5 13.4631 12.6519C13.0724 12.8309 12.7195 13.2194 12.5789 13.6255C12.4596 13.9702 12.4938 14.3245 12.5622 15.0331C13.2899 22.5752 17.4248 26.7101 24.9669 27.4378C25.6755 27.5063 26.0298 27.5404 26.3745 27.4211C26.7806 27.2805 27.1692 26.9276 27.3481 26.5369C27.5 26.2052 27.5 25.8101 27.5 25.0198C27.5 24.4796 27.5 24.2095 27.4268 23.9703C27.3351 23.6704 27.1609 23.4024 26.9242 23.1968C26.7352 23.0328 26.4884 22.9231 25.9948 22.7037L25.2398 22.3681C24.7052 22.1305 24.4379 22.0118 24.1663 21.9859C23.9063 21.9612 23.6443 21.9977 23.4009 22.0924C23.1467 22.1914 22.9219 22.3787 22.4725 22.7532C22.0251 23.126 21.8014 23.3124 21.5281 23.4123C21.2858 23.5008 20.9655 23.5336 20.7103 23.4959C20.4224 23.4535 20.2019 23.3358 19.7611 23.1001C18.3893 22.3671 17.6329 21.6107 16.8999 20.2389C16.6643 19.7981 16.5465 19.5776 16.5041 19.2898C16.4665 19.0345 16.4992 18.7142 16.5878 18.4719C16.6876 18.1986 16.874 17.9749 17.2468 17.5275C17.6214 17.0781 17.8086 16.8534 17.9076 16.5991C18.0024 16.3558 18.0388 16.0937 18.0141 15.8337C17.9883 15.5621 17.8695 15.2948 17.6319 14.7602Z"
          stroke="#4B5563"
          stroke-width="1.25"
          stroke-linecap="round"
        />
        <path
          d="M23.3268 10.8333H33.3268M31.6602 7.5L34.9935 10.8333L31.6602 14.1667M24.9935 7.5L21.6602 10.8333L24.9935 14.1667"
          stroke="#4B5563"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    type: 'Custom Tool',
    name: 'Custom Tool',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M26.0005 9.99976V17.9998M30.0005 13.9998H22.0005M10.5005 13.9998C10.5005 12.4598 10.5005 11.6898 10.8475 11.1238C11.0415 10.8068 11.3075 10.5408 11.6245 10.3468C12.1895 9.99976 12.9605 9.99976 14.5005 9.99976C16.0405 9.99976 16.8105 9.99976 17.3765 10.3468C17.6935 10.5408 17.9595 10.8068 18.1535 11.1238C18.5005 11.6888 18.5005 12.4598 18.5005 13.9998C18.5005 15.5398 18.5005 16.3098 18.1535 16.8758C17.9595 17.1928 17.6935 17.4588 17.3765 17.6528C16.8115 17.9998 16.0405 17.9998 14.5005 17.9998C12.9605 17.9998 12.1905 17.9998 11.6245 17.6528C11.3078 17.4588 11.0415 17.1925 10.8475 16.8758C10.5005 16.3108 10.5005 15.5398 10.5005 13.9998ZM11.7825 22.7818C12.8295 21.7348 13.3525 21.2118 13.9725 21.0618C14.3196 20.9784 14.6815 20.9784 15.0285 21.0618C15.6485 21.2118 16.1715 21.7348 17.2185 22.7818C18.2655 23.8288 18.7885 24.3518 18.9385 24.9718C19.0204 25.319 19.0204 25.6805 18.9385 26.0278C18.7885 26.6478 18.2655 27.1718 17.2185 28.2178C16.1715 29.2638 15.6485 29.7878 15.0285 29.9378C14.6815 30.0211 14.3196 30.0211 13.9725 29.9378C13.3525 29.7878 12.8295 29.2648 11.7825 28.2178C10.7355 27.1708 10.2125 26.6478 10.0625 26.0278C9.97915 25.6807 9.97915 25.3188 10.0625 24.9718C10.2125 24.3518 10.7355 23.8278 11.7825 22.7818ZM22.0005 25.9998C22.0005 24.4598 22.0005 23.6898 22.3475 23.1238C22.5415 22.8068 22.8075 22.5408 23.1245 22.3468C23.6895 21.9998 24.4605 21.9998 26.0005 21.9998C27.5405 21.9998 28.3105 21.9998 28.8775 22.3468C29.1935 22.5408 29.4595 22.8068 29.6535 23.1238C30.0005 23.6888 30.0005 24.4598 30.0005 25.9998C30.0005 27.5398 30.0005 28.3098 29.6535 28.8768C29.4594 29.1928 29.1936 29.4587 28.8775 29.6528C28.3105 29.9998 27.5405 29.9998 26.0005 29.9998C24.4605 29.9998 23.6905 29.9998 23.1245 29.6528C22.8079 29.4591 22.5416 29.1932 22.3475 28.8768C22.0005 28.3098 22.0005 27.5398 22.0005 25.9998Z"
          stroke="#4B5563"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    type: 'Press Button',
    name: 'Press Button',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M17.8126 12.8126V12.1876C17.8126 12.0218 17.8784 11.8628 17.9956 11.7456C18.1128 11.6284 18.2718 11.5626 18.4376 11.5626C18.6033 11.5626 18.7623 11.6284 18.8795 11.7456C18.9967 11.8628 19.0626 12.0218 19.0626 12.1876V12.8126C19.0626 12.9783 18.9967 13.1373 18.8795 13.2545C18.7623 13.3717 18.6033 13.4376 18.4376 13.4376C18.2718 13.4376 18.1128 13.3717 17.9956 13.2545C17.8784 13.1373 17.8126 12.9783 17.8126 12.8126ZM12.1876 19.0626H12.8126C12.9783 19.0626 13.1373 18.9967 13.2545 18.8795C13.3717 18.7623 13.4376 18.6033 13.4376 18.4376C13.4376 18.2718 13.3717 18.1128 13.2545 17.9956C13.1373 17.8784 12.9783 17.8126 12.8126 17.8126H12.1876C12.0218 17.8126 11.8628 17.8784 11.7456 17.9956C11.6284 18.1128 11.5626 18.2718 11.5626 18.4376C11.5626 18.6033 11.6284 18.7623 11.7456 18.8795C11.8628 18.9967 12.0218 19.0626 12.1876 19.0626ZM20.6579 13.9969C20.7313 14.0337 20.8113 14.0557 20.8932 14.0615C20.9752 14.0674 21.0574 14.057 21.1354 14.031C21.2133 14.005 21.2853 13.964 21.3474 13.9101C21.4094 13.8563 21.4602 13.7907 21.4969 13.7172L22.1219 12.4672C22.1961 12.319 22.2084 12.1473 22.156 11.9901C22.1036 11.8328 21.9909 11.7027 21.8426 11.6286C21.6944 11.5544 21.5227 11.5421 21.3654 11.5945C21.2082 11.6469 21.0781 11.7596 21.004 11.9079L20.379 13.1579C20.3421 13.2313 20.3202 13.3112 20.3143 13.3931C20.3084 13.475 20.3186 13.5573 20.3445 13.6352C20.3704 13.7131 20.4115 13.7852 20.4652 13.8472C20.519 13.9093 20.5844 13.9602 20.6579 13.9969ZM13.1579 20.379L11.9079 21.004C11.7596 21.0781 11.6469 21.2082 11.5945 21.3654C11.5421 21.5227 11.5544 21.6944 11.6286 21.8426C11.7027 21.9909 11.8328 22.1036 11.9901 22.156C12.1473 22.2084 12.319 22.1961 12.4672 22.1219L13.7172 21.4969C13.7906 21.4602 13.8561 21.4094 13.9099 21.3473C13.9636 21.2853 14.0046 21.2133 14.0306 21.1354C14.0565 21.0576 14.0669 20.9753 14.061 20.8935C14.0552 20.8116 14.0333 20.7317 13.9965 20.6583C13.9598 20.5848 13.909 20.5194 13.847 20.4656C13.7849 20.4119 13.7129 20.3708 13.635 20.3449C13.5572 20.319 13.4749 20.3086 13.3931 20.3145C13.3112 20.3203 13.2313 20.3422 13.1579 20.379ZM28.0711 25.3126C28.1872 25.4286 28.2793 25.5664 28.3422 25.7181C28.405 25.8698 28.4374 26.0324 28.4374 26.1965C28.4374 26.3607 28.405 26.5233 28.3422 26.675C28.2793 26.8266 28.1872 26.9644 28.0711 27.0805L27.0805 28.0711C26.9644 28.1872 26.8266 28.2793 26.675 28.3422C26.5233 28.405 26.3607 28.4374 26.1965 28.4374C26.0324 28.4374 25.8698 28.405 25.7181 28.3422C25.5664 28.2793 25.4286 28.1872 25.3126 28.0711L21.3047 24.0626L28.0711 25.3126ZM27.1876 26.1961L23.179 22.1876C23.0347 22.0435 22.9278 21.8664 22.8677 21.6716C22.8075 21.4768 22.7959 21.2703 22.8339 21.07C22.8719 20.8697 22.9583 20.6817 23.0856 20.5225C23.2128 20.3632 23.3771 20.2375 23.5641 20.1563L23.5891 20.1454L27.179 18.7672L14.6876 14.6876L18.7657 27.1766L20.1446 23.5829C20.1446 23.5743 20.1508 23.5657 20.1547 23.5571C20.236 23.3702 20.3616 23.2061 20.5208 23.0789C20.68 22.9517 20.8679 22.8654 21.068 22.8274C21.1453 22.8131 21.2238 22.8057 21.3024 22.8055C21.6336 22.8059 21.9511 22.9377 22.1852 23.1719L26.1961 27.1876L27.1876 26.1961Z"
          fill="#4B5563"
        />
      </svg>
    ),
  },
  {
    type: 'Route',
    name: 'Route',
    icon: <CiRoute size={24} />,
  },
  {
    type: 'SMS',
    name: 'SMS',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M16.4997 22.5004H23.4997M16.4997 17.5004H19.9997M22.1697 28.8904C26.3537 28.6134 29.6857 25.2334 29.9597 20.9904C30.0127 20.1604 30.0127 19.3004 29.9597 18.4704C29.6857 14.2284 26.3537 10.8504 22.1697 10.5714C20.7246 10.4762 19.2748 10.4762 17.8297 10.5714C13.6457 10.8494 10.3137 14.2284 10.0397 18.4714C9.98676 19.3106 9.98676 20.1523 10.0397 20.9914C10.1397 22.5364 10.8227 23.9674 11.6277 25.1754C12.0947 26.0204 11.7867 27.0754 11.2997 27.9984C10.9497 28.6634 10.7737 28.9954 10.9147 29.2354C11.0547 29.4754 11.3697 29.4834 11.9987 29.4984C13.2437 29.5284 14.0827 29.1764 14.7487 28.6854C15.1257 28.4064 15.3147 28.2674 15.4447 28.2514C15.5747 28.2354 15.8317 28.3414 16.3437 28.5514C16.8037 28.7414 17.3387 28.8584 17.8287 28.8914C19.2537 28.9854 20.7427 28.9854 22.1707 28.8914"
          stroke="#4B5563"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
    enterprise: true,
  },
  {
    type: 'Amazon Connect',
    name: 'Amazon Connect',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M30 20C30 25.5228 25.5228 30 20 30C14.4772 30 10 25.5228 10 20C10 14.4772 14.4772 10 20 10C25.5228 10 30 14.4772 30 20Z"
          stroke="#4B5563"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20 15V25M15 20H25"
          stroke="#4B5563"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M26 14L29 11M11 29L14 26M14 14L11 11M29 29L26 26"
          stroke="#4B5563"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    enterprise: true,
  },
  {
    type: 'Twilio Flow Redirect',
    name: 'Twilio Flow Redirect',
    icon: (
      <svg
        role="img"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        aria-labelledby="ProductStudioIcon-87"
      >
        <path
          fill="currentColor"
          fill-rule="evenodd"
          d="M4 6h13.5a.5.5 0 00.5-.5v-3a.5.5 0 00-.5-.5h-6a.5.5 0 00-.5.5V5H4a2 2 0 100 4h2v2.5a.5.5 0 00.5.5H16a1 1 0 010 2H2.5a.5.5 0 00-.5.5v3a.5.5 0 00.5.5h6a.5.5 0 00.5-.5V15h7a2 2 0 100-4h-2V8.5a.5.5 0 00-.5-.5H4a1 1 0 110-2zm8-3h5v2h-5V3zM8 17H3v-2h5v2zm5-6H7V9h6v2z"
        >
        </path>
      </svg>
    ),
    enterprise: true,
  },
  {
    type: 'Transfer Chat',
    name: 'Transfer SMS Chat Node',
    icon: (
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M16.4997 22.5004H23.4997M16.4997 17.5004H19.9997M22.1697 28.8904C26.3537 28.6134 29.6857 25.2334 29.9597 20.9904C30.0127 20.1604 30.0127 19.3004 29.9597 18.4704C29.6857 14.2284 26.3537 10.8504 22.1697 10.5714C20.7246 10.4762 19.2748 10.4762 17.8297 10.5714C13.6457 10.8494 10.3137 14.2284 10.0397 18.4714C9.98676 19.3106 9.98676 20.1523 10.0397 20.9914C10.1397 22.5364 10.8227 23.9674 11.6277 25.1754C12.0947 26.0204 11.7867 27.0754 11.2997 27.9984C10.9497 28.6634 10.7737 28.9954 10.9147 29.2354C11.0547 29.4754 11.3697 29.4834 11.9987 29.4984C13.2437 29.5284 14.0827 29.1764 14.7487 28.6854C15.1257 28.4064 15.3147 28.2674 15.4447 28.2514C15.5747 28.2354 15.8317 28.3414 16.3437 28.5514C16.8037 28.7414 17.3387 28.8584 17.8287 28.8914C19.2537 28.9854 20.7427 28.9854 22.1707 28.8914"
          stroke="#4B5563"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
    beta: true,
  },
  {
    type: 'IVR',
    name: 'IVR Navigation',
    icon: (
      <Network size={24} />

    ),
  },
  {
    type: 'Custom Code',
    name: 'Custom Code',
    icon: (
      <Code size={24} />
    ),
    enterprise: true,
  },
  {
    type: 'Scheduling',
    name: 'Scheduling Node (Beta)',
    icon: (
      <Calendar size={24} />
    ),
    enterprise: true,
  },
]

function FeaturedNodeDisplay({ featuredNode, isLoading, onSelectNodeType, setActiveTab }) {
  if (isLoading || !featuredNode) {
    return (
      <Skeleton className="h-[180px] w-full" />
    )
  }
  return (
    <div className="border-b border-gray-200 bg-gray-50 py-4">
      <div className="px-4">
        <div className="flex flex-col">
          <div className="mb-3 flex items-center justify-end">
            <button
              onClick={() => setActiveTab('templates')}
              className={`
                rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white
                hover:bg-indigo-700
              `}
            >
              View More In Node Library →
            </button>
          </div>
          <div className="flex justify-center">
            <div className={`
              max-w-[400px] overflow-hidden rounded-lg border border-gray-200
              bg-white shadow-xs
            `}
            >
              <NodePreviewCard
                showStatusBadge={false}
                node={featuredNode}
                onInsert={onSelectNodeType}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function FeaturedNodeAccordion({ featuredNode, isLoading, onSelectNodeType, setActiveTab }) {
  const initializedRef = useRef(false)
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const savedState = localStorage.getItem('nodeFeaturedAccordionState')
      return savedState !== null ? JSON.parse(savedState) : true
    }
    catch (error) {
      console.error('Error reading from localStorage:', error)
      return true
    }
  })

  useEffect(() => {
    if (initializedRef.current) {
      try {
        localStorage.setItem('nodeFeaturedAccordionState', JSON.stringify(isOpen))
      }
      catch (error) {
        console.error('Error saving to localStorage:', error)
      }
    }
    else {
      initializedRef.current = true
    }
  }, [isOpen])

  return (
    <div className={`
      w-full rounded border border-gray-300 p-2.5 text-gray-600
      focus:ring-2 focus:ring-blue-500 focus:outline-hidden
    `}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between"
      >
        <span className={`
          inline-flex items-center text-sm font-normal text-gray-400
        `}
        >
          <StarIcon className="mr-1 h-4 w-4" />
          Featured Node
        </span>
        <svg
          className={`
            h-5 w-5 transition-transform
            ${isOpen ? 'rotate-180' : ''}
          `}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="mt-2">
          <FeaturedNodeDisplay featuredNode={featuredNode} isLoading={isLoading} onSelectNodeType={onSelectNodeType} setActiveTab={setActiveTab} />
        </div>
      )}
    </div>
  )
}

function AddNewNodeSlideOut({ isOpen, onClose, onSelectNodeType }) {
  const { currentOpenNodeID } = useContext(LibrarySidebarContext)
  const [searchTerm, setSearchTerm] = useState('')
  const [hoveredNode, setHoveredNode] = useState(null)
  const [activeTab, setActiveTab] = useState('basic')

  const { data: nodes, isLoading } = useQuery({
    queryKey: ['nodesLibrary'],
    queryFn: async () => {
      const { data } = await $fetch('/v1/pathway/library/nodes')
      return data
    },
    staleTime: Infinity,
  })

  const featuredNode = useMemo(() => {
    if (nodes && nodes.length > 0) {
      return nodes.find(node => node.featured_at)
    }
    return null
  }, [nodes])

  useEffect(() => {
    if (currentOpenNodeID) {
      setActiveTab('templates')
    }
  }, [currentOpenNodeID])

  const { client } = useStatsigClient()
  const { org } = useAuth()
  const isEnterprise = org?.org_type === 'enterprise'
  const isSMSPathwaysEnabled = client.checkGate('sms_pathways')

  const filteredNodeTypes = nodeTypes
    .filter(node =>
      node.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .filter((node) => {
      if (node.name !== 'Schedule Meeting') {
        return true
      }
      else {
        return client.checkGate('calendly_testing')
      }
    })

  const handleNodeClick = (nodeType) => {
    if (
      !nodeType.enterprise & !nodeType.beta
      || isEnterprise & !nodeType.beta
      || isSMSPathwaysEnabled
    ) {
      onSelectNodeType(nodeType.type)
    }
  }

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: isOpen ? 0 : '100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`
        fixed top-0 right-0 z-50 h-full w-2/5 overflow-y-auto bg-white shadow-lg
      `}
    >
      <div
        style={{ height: 53.5 }}
        className={`
          flex items-center justify-between border-b border-gray-200 p-4
        `}
      >
        <h2 className="text-base font-semibold text-gray-800">Add New Node</h2>
        <button
          onClick={onClose}
          className={`
            text-gray-500
            hover:text-gray-700
          `}
        >
          <X style={{ width: 20, height: 20 }} />
        </button>
      </div>

      <div className="p-4">

        <div className="relative mb-4">
          <input
            type="text"
            placeholder="Search Node"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={`
              w-full rounded border border-gray-300 py-1.5 pr-2.5 pl-6
              text-gray-600
              focus:ring-2 focus:ring-blue-500 focus:outline-hidden
            `}
          />
          <MagnifyingGlassIcon className={`
            absolute top-2.5 left-2 h-3 w-3 text-gray-400
          `}
          />
        </div>

        <FeaturedNodeAccordion featuredNode={featuredNode} isLoading={isLoading} onSelectNodeType={onSelectNodeType} setActiveTab={setActiveTab} />

        <div className="mt-4 mb-4 flex gap-x-0.5 border-b border-gray-200">
          <button
            className={`
              rounded-t-lg px-2.5 py-1.5 text-xs font-medium transition-colors
              duration-200
              ${
    activeTab === 'basic'
      ? 'border-b-2 border-indigo-600 bg-indigo-50 text-indigo-600'
      : `
        text-gray-500
        hover:bg-gray-50 hover:text-gray-700
      `
    }
            `}
            onClick={() => setActiveTab('basic')}
          >
            Basic Nodes
          </button>
          <button
            className={`
              rounded-t-lg px-2.5 py-1.5 text-xs font-medium transition-colors
              duration-200
              ${
    activeTab === 'templates'
      ? 'border-b-2 border-indigo-600 bg-indigo-50 text-indigo-600'
      : `
        text-gray-500
        hover:bg-gray-50 hover:text-gray-700
      `
    }
            `}
            onClick={() => setActiveTab('templates')}
          >
            Node Library
          </button>
        </div>

        {activeTab === 'basic' && (
          <div className="grid grid-cols-2 gap-2.5">
            {filteredNodeTypes.map(nodeType => (
              <div
                key={nodeType.type}
                className={`
                  relative flex flex-col items-center justify-center rounded
                  border border-gray-200 p-2.5 transition-all duration-200
                  ${
              hoveredNode === nodeType.type
              && (!nodeType.enterprise
                || isEnterprise
                || nodeType.beta
                || isSMSPathwaysEnabled)
                ? 'border-blue-300 bg-blue-50'
                : 'hover:bg-gray-50'
              }
                  ${
              (nodeType.enterprise && !isEnterprise)
              || (nodeType.beta && !isSMSPathwaysEnabled)
                ? 'opacity-50'
                : 'hover:border hover:border-blue-300'
              }
                `}
                onMouseEnter={() => setHoveredNode(nodeType.type)}
                onMouseLeave={() => setHoveredNode(null)}
                onClick={() => handleNodeClick(nodeType)}
              >
                <div className="mb-1.5 text-gray-600">{nodeType.icon}</div>
                <span className="text-center text-xs">{nodeType.name}</span>
                {nodeType.enterprise && (
                  <span className={`
                    absolute top-0.5 right-0.5 rounded-sm bg-purple-100 px-1.5
                    py-0.5 text-2xs text-blue-800
                  `}
                  >
                    Enterprise
                  </span>
                )}
                {nodeType.beta && (
                  <span className={`
                    absolute top-0.5 right-0.5 rounded-sm bg-purple-100 px-1.5
                    py-0.5 text-2xs text-blue-800
                  `}
                  >
                    Beta
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'templates' && (
          <PreConfiguredNodesTab
            nodesLibrary={nodes}
            searchTerm={searchTerm}
            onSelectNode={onSelectNodeType}
            setHoveredNode={setHoveredNode}
            isLoading={isLoading}
          />
        )}
      </div>

      {hoveredNode && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className={`
            fixed bottom-2.5 left-1/2 -translate-x-1/2 transform rounded
            bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg
          `}
        >
          {nodeTypes.find(n => n.type === hoveredNode)?.beta
            ? ''
            : activeTab === 'basic'
              && nodeTypes.find(n => n.type === hoveredNode)?.isEnterprise
              && !isEnterprise
              ? 'Contact Sales for Enterprise Plan'
              : hoveredNode === 'Amazon Connect'
                ? 'Use Amazon Connect Node to integrate with Amazon Connect for handling DTMF tones and enhanced call routing capabilities.'
                : `Use ${hoveredNode} Node to connect your agent to a ${hoveredNode?.toLowerCase()}, to answer any questions the user has.`}
          {activeTab === 'basic'
            && nodeTypes.find(n => n.type === hoveredNode)?.beta
            && !isSMSPathwaysEnabled
            ? 'Contact Us For Beta Access'
            : `Use ${hoveredNode} Node to transfer the SMS conversation to a human agent.`}
        </motion.div>
      )}
    </motion.div>
  )
}

export default AddNewNodeSlideOut
